import React, { useState } from 'react';
import { Button } from 'antd';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { ObjectCardTile } from '../../components/layout/ObjectCard';
import type { ObjectCardTone } from '../../components/layout/ObjectCard';
import './ReportTab.css';

/**
 * Report tab primitives (modules/reports/DESIGN.md „Anatomy of a report tab”)
 * in the 2026-10-19 direction: icon tiles from the object cards, one line per
 * text with the full text in `title`, sections as rounded cards.
 */

/** Plain text of a node for a `title` tooltip when one line cuts it. */
export const reportTextOf = (node: React.ReactNode): string | undefined => {
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) {
    const text = node.map(reportTextOf).filter(Boolean).join('');
    return text || undefined;
  }
  if (React.isValidElement<{ children?: React.ReactNode }>(node)) return reportTextOf(node.props.children);
  return undefined;
};

interface ReportFigureProps {
  label: React.ReactNode;
  value: React.ReactNode;
  /** One muted line saying what the number includes. */
  note?: React.ReactNode;
  icon: React.ReactNode;
  tone?: ObjectCardTone;
  /** `is-negative`, `is-positive` or `is-missing` where the sign carries meaning. */
  valueClassName?: string;
  testId?: string;
  /** The tile opens what it is made of (Pulpit „Skąd ta liczba”). */
  onClick?: () => void;
  selected?: boolean;
  /** Full text of the note when it says more than one line (title). */
  noteTitle?: string;
}

/** One of the 2–4 numbers a tab is about. */
export const ReportFigure: React.FC<ReportFigureProps> = ({
  label, value, note, icon, tone = 'blue', valueClassName, testId, onClick, selected, noteTitle,
}) => (
  <div
    className={`report-figure${onClick ? ' is-clickable' : ''}${selected ? ' is-selected' : ''}`}
    data-testid={testId}
    {...(onClick ? {
      role: 'button',
      tabIndex: 0,
      'aria-pressed': selected === true,
      onClick,
      onKeyDown: (event: React.KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onClick();
        }
      },
    } : {})}
  >
    <ObjectCardTile tone={tone}>{icon}</ObjectCardTile>
    <div className="report-figure__copy">
      <span className="report-figure__label" title={reportTextOf(label)}>{label}</span>
      <strong className={`report-figure__value${valueClassName ? ` ${valueClassName}` : ''}`} title={reportTextOf(value)}>
        {value}
      </strong>
      {note && <span className="report-figure__note" title={noteTitle ?? reportTextOf(note)}>{note}</span>}
    </div>
  </div>
);

interface ReportSectionProps {
  title: React.ReactNode;
  icon?: React.ReactNode;
  tone?: ObjectCardTone;
  count?: number;
  /** One sentence of how to read the section (one line, full text in `title`). */
  lead?: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}

/** A rounded card with a small icon tile, a 15 px title and one sentence. */
export const ReportSection: React.FC<ReportSectionProps> = ({
  title, icon, tone = 'blue', count, lead, actions, className, children,
}) => (
  <section className={`report-section${className ? ` ${className}` : ''}`}>
    <header className="report-section__head">
      {icon && <ObjectCardTile tone={tone} size="sm">{icon}</ObjectCardTile>}
      <h3 className="report-section__title" title={reportTextOf(title)}>{title}</h3>
      {count !== undefined && <span className="report-section__count">{count}</span>}
      {actions && <div className="report-section__actions">{actions}</div>}
    </header>
    {lead && <p className="report-section__lead" title={reportTextOf(lead)}>{lead}</p>}
    {children}
  </section>
);

interface CostPartialMarkProps {
  /** What the mark means (title and aria-label). */
  hint: string;
  testId?: string;
}

/**
 * The one look of „*” for an amount that holds an incomplete cost — the known
 * part summed and marked (owner decisions 2026-09-30 and 2026-10-29). Used by
 * the Pulpit, Rachunek wyników, the pivot and „Prognoza a wynik”.
 */
export const CostPartialMark: React.FC<CostPartialMarkProps> = ({ hint, testId = 'cost-partial-mark' }) => (
  <sup className="report-partial" title={hint} aria-label={hint} data-testid={testId}>*</sup>
);

/** A table with more rows than this starts collapsed (owner 2026-10-30). */
export const LONG_TABLE_ROWS = 10;

interface ReportLongTableProps {
  /** Rows the table shows now (after filters); a short table renders as is. */
  rowCount: number;
  testId?: string;
  children: React.ReactNode;
}

/**
 * The one way a long table of „Wynik firmy” is collapsed: the tabs show their
 * figures and charts first, a table opens on „Pokaż tabelę” (owner 2026-10-30).
 */
export const ReportLongTable: React.FC<ReportLongTableProps> = ({ rowCount, testId = 'report-long-table', children }) => {
  const { t } = useTranslation('reports');
  const [open, setOpen] = useState(false);
  if (rowCount <= LONG_TABLE_ROWS) return <>{children}</>;
  return (
    <div className={`report-long-table${open ? ' is-open' : ''}`} data-testid={testId}>
      <Button
        type="link"
        size="small"
        className="report-long-table__toggle"
        icon={open ? <UpOutlined /> : <DownOutlined />}
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        data-testid="report-long-table-toggle"
      >
        {open ? t('longTable.hide') : t('longTable.show', { count: rowCount })}
      </Button>
      {open && children}
    </div>
  );
};
